import { faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, InputNumber, Select, Skeleton, Space, Typography } from "antd"
import { isEqual } from "lodash"
import { ReactNode, useEffect, useMemo, useReducer } from "react"
import { sendTimeOptions } from "../../../common/timeOptions"
import { emptyInvoiceReminderSchedule, toEditableSchedule } from "../../../domain/invoicing/settings/invoiceReminderSchedule"
import { useInvoiceReminderScheduleQuery, useUpdateInvoiceReminderScheduleMutation } from "../../../domain/invoicing/settings/invoiceReminderScheduleQueries"
import EmailTemplatePreview from "../../EmailTemplates/EmailTemplatePreview"
import { useEmailTemplatesQuery } from "../../EmailTemplates/emailTemplateQueries"
import { InvoiceReminderTokens } from "../../RichTextEditor/tokenModel"
import LabeledSwitch from "../../common/LabeledSwitch"
import VSpace from "../../common/VSpace"
import SettingsCard from "../SettingsCard"
import { anchorOptions, findInvoiceReminderTemplate, getInvoiceReminderTemplateOptions, toView } from "./invoiceReminderScheduleModel"
import { reducer } from "./invoiceReminderScheduleReducer"

type Props = {
  additionalActions?: ReactNode
}

const InvoiceReminderScheduleCard = ({ additionalActions }: Props) => {
  const scheduleQuery = useInvoiceReminderScheduleQuery()
  const emailTemplatesQuery = useEmailTemplatesQuery()
  const updateMutation = useUpdateInvoiceReminderScheduleMutation()
  const [state, dispatch] = useReducer(reducer, {
    serverSchedule: emptyInvoiceReminderSchedule(),
    schedule: emptyInvoiceReminderSchedule()
  })

  const settings = scheduleQuery.data

  useEffect(() => {
    if (settings) {
      dispatch({ type: 'SERVER_SCHEDULE_LOADED', value: toEditableSchedule(settings) })
    }
  }, [settings])

  const emailTemplates = emailTemplatesQuery.data ?? []
  const range = useMemo(
    () => ({ minDays: settings?.minDays ?? 0, maxDays: settings?.maxDays ?? 0 }),
    [settings?.minDays, settings?.maxDays]
  )
  const view = useMemo(() => toView(state.schedule, emailTemplates, range), [state.schedule, emailTemplates, range])
  const templateOptions = useMemo(() => getInvoiceReminderTemplateOptions(emailTemplates), [emailTemplates])
  const isChanged = !isEqual(state.schedule, state.serverSchedule)
  const isLoading = emailTemplatesQuery.isLoading

  if (!settings?.available) {
    return null
  }

  return <SettingsCard
    title="Automatic Invoice Reminders"
    description="Remind customers about invoices you have emailed them, on a schedule anchored to the invoice due date. Reminders stop as soon as the invoice is paid."
    onUpdate={() => updateMutation.mutate(state.schedule)}
    isUpdating={updateMutation.isLoading}
    updateDisabled={!isChanged || view.hasErrors}
    additionalActions={additionalActions}
  >
    {isLoading && <Skeleton active />}
    {!isLoading && <VSpace size="middle">
      <LabeledSwitch
        label="Send automatic invoice reminder emails"
        value={view.isEnabled}
        onChange={value => dispatch({ type: 'ENABLED_CHANGED', value })}
      />
      {view.steps.map(step => <VSpace key={step.id} size="small">
        <Space wrap>
          <span>Send a reminder email</span>
          {step.showDays && <>
            <InputNumber
              value={step.days}
              precision={0}
              style={{ width: '70px' }}
              status={step.daysError ? 'error' : step.daysWarning ? 'warning' : undefined}
              aria-label="Days from the due date"
              onChange={value => {
                if (value !== null) {
                  dispatch({ type: 'DAYS_CHANGED', id: step.id, value })
                }
              }}
            />
            <span>day{step.days === 1 ? '' : 's'}</span>
          </>}
          <Select
            value={step.anchor}
            options={anchorOptions}
            popupMatchSelectWidth={false}
            aria-label="Due date anchor"
            onChange={value => dispatch({ type: 'ANCHOR_CHANGED', id: step.id, value })}
          />
          <span>the due date at</span>
          <Select
            value={step.atTime}
            options={sendTimeOptions}
            popupMatchSelectWidth={false}
            aria-label="Send time"
            onChange={value => dispatch({ type: 'AT_TIME_CHANGED', id: step.id, value })}
          />
          <span>using template</span>
          <Select
            value={step.templateError ? undefined : step.emailTemplateGuid}
            options={templateOptions}
            placeholder="Select a template"
            style={{ width: '180px' }}
            popupMatchSelectWidth={false}
            status={step.templateError ? 'error' : undefined}
            aria-label="Reminder template"
            onChange={value => dispatch({ type: 'EMAIL_TEMPLATE_CHANGED', id: step.id, value })}
          />
          <EmailTemplatePreview
            template={findInvoiceReminderTemplate(emailTemplates, step.emailTemplateGuid)}
            tokens={InvoiceReminderTokens}
          />
          <Button
            icon={<FontAwesomeIcon icon={faTrash} />}
            danger
            type="link"
            aria-label="Delete reminder"
            onClick={() => dispatch({ type: 'REMINDER_REMOVED', id: step.id })}
          />
        </Space>
        {step.daysError && <Typography.Text type="danger">{step.daysError}</Typography.Text>}
        {step.templateError && <Typography.Text type="danger">{step.templateError}</Typography.Text>}
        {step.daysWarning && <Typography.Text type="warning">{step.daysWarning}</Typography.Text>}
      </VSpace>)}
      <div>
        <Button onClick={() => dispatch({ type: 'REMINDER_ADDED', range })}>Add Another Reminder</Button>
      </div>
    </VSpace>}
  </SettingsCard>
}

export default InvoiceReminderScheduleCard
